import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/SearchAutocomplete.module.css';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const defaultSuggestUrl = (query) => `/api/deals/suggest?q=${encodeURIComponent(query)}`;
const defaultExtractItems = (data) => (Array.isArray(data) ? data : []);
const defaultGetOptionKey = (item) => item.id;

const renderDealOption = (deal) => (
    <>
        <img src={deal.image} alt="" className={styles.optionImage} loading="lazy" />
        <span className={styles.optionInfo}>
            <span className={styles.optionName}>{deal.name}</span>
            <span className={styles.optionMeta}>
                {deal.store}
                {Number(deal.discount) > 0 && ` · -${Number(deal.discount)}%`}
            </span>
        </span>
        <span className={styles.optionPrice}>{Number(deal.price || 0).toFixed(2)}€</span>
    </>
);

// Reutilizado por el buscador de la home (deals, /api/deals/suggest) y por el
// de cupones (Coupons.js, /api/coupons) -- suggestUrl/extractItems/renderOption
// /onSelectOption son los puntos de extension; los defaults son los de deals
// para no tener que tocar Home.js al añadir esto.
const SearchAutocomplete = ({
    value,
    onChange,
    onSubmit,
    placeholder,
    ariaLabel = 'Buscar gangas',
    inputRef,
    inputClassName,
    id,
    suggestUrl = defaultSuggestUrl,
    extractItems = defaultExtractItems,
    getOptionKey = defaultGetOptionKey,
    renderOption = renderDealOption,
    onSelectOption,
    minLength = MIN_QUERY_LENGTH,
    showViewAll = true
}) => {
    const navigate = useNavigate();
    const listboxId = useId();
    const containerRef = useRef(null);
    const debounceRef = useRef(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadedQuery, setLoadedQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isBroadSearch, setIsBroadSearch] = useState(false);

    useEffect(() => {
        const trimmed = value.trim();

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        let controller;
        setLoadedQuery('');
        setLoadFailed(false);

        if (trimmed.length < minLength) {
            setSuggestions([]);
            setIsLoading(false);
            setHighlightedIndex(-1);
            setIsBroadSearch(false);
            return undefined;
        }

        debounceRef.current = setTimeout(() => {
            controller = new AbortController();
            setIsLoading(true);

            fetch(suggestUrl(trimmed), { signal: controller.signal })
                .then((response) => {
                    if (!response.ok) {
                        return Promise.reject(new Error('suggest-failed'));
                    }
                    // Cabecera opcional: solo la pone /api/deals/suggest cuando el
                    // termino es de una sola palabra y el planner estima muchas
                    // filas coincidentes -- nunca oculta ni bloquea resultados,
                    // solo permite avisar de que afinar la busqueda ira mas rapido.
                    const broad = response.headers?.get?.('X-Esganga-Broad-Search') === 'true';
                    return response.json().then((data) => ({ data, broad }));
                })
                .then(({ data, broad }) => {
                    if (controller.signal.aborted) {
                        return;
                    }
                    setSuggestions(extractItems(data));
                    setIsBroadSearch(broad);
                    setHighlightedIndex(-1);
                    setLoadedQuery(trimmed);
                    setLoadFailed(false);
                })
                .catch((error) => {
                    if (error.name !== 'AbortError') {
                        setSuggestions([]);
                        setIsBroadSearch(false);
                        setLoadedQuery(trimmed);
                        setLoadFailed(true);
                    }
                })
                .finally(() => {
                    if (!controller.signal.aborted) {
                        setIsLoading(false);
                    }
                });
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (controller) {
                controller.abort();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, minLength]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const trimmedValue = value.trim();
    const hasSuggestions = trimmedValue.length >= minLength && suggestions.length > 0;
    const hasLoadedCurrentQuery = loadedQuery === trimmedValue;
    const showDropdown = isOpen && trimmedValue.length >= minLength && (isLoading || hasLoadedCurrentQuery);
    const optionsCount = suggestions.length + (hasSuggestions && showViewAll ? 1 : 0);
    const activeOptionId = highlightedIndex >= 0
        ? `${listboxId}-option-${highlightedIndex}`
        : undefined;
    const showBroadHint = hasLoadedCurrentQuery && !loadFailed && isBroadSearch;
    const liveMessage = isLoading
        ? 'Buscando sugerencias'
        : hasLoadedCurrentQuery
            ? loadFailed
                ? 'No se han podido cargar las sugerencias. Puedes buscar igualmente.'
                : suggestions.length > 0
                    ? `${suggestions.length} sugerencias disponibles${showBroadHint ? '. Búsqueda amplia: añade marca, modelo o categoría para ir más rápido' : ''}`
                    : `Sin sugerencias para ${trimmedValue}`
            : '';

    const selectOption = (item) => {
        setIsOpen(false);
        if (onSelectOption) {
            onSelectOption(item);
        } else {
            navigate(`/ofertas/${item.id}`, { state: { deal: item } });
        }
    };

    const submitSearch = () => {
        setIsOpen(false);
        onSubmit(trimmedValue);
    };

    const handleKeyDown = (event) => {
        if (!showDropdown || optionsCount === 0) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((index) => (index + 1) % optionsCount);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((index) => (index - 1 + optionsCount) % optionsCount);
            return;
        }

        if (event.key === 'Escape') {
            setIsOpen(false);
            return;
        }

        if (event.key === 'Enter' && highlightedIndex >= 0) {
            event.preventDefault();
            if (highlightedIndex < suggestions.length) {
                selectOption(suggestions[highlightedIndex]);
            } else {
                submitSearch();
            }
        }
    };

    return (
        <div className={styles.wrapper} ref={containerRef}>
            <input
                id={id}
                ref={inputRef}
                type="search"
                value={value}
                placeholder={placeholder}
                aria-label={ariaLabel}
                role="combobox"
                aria-expanded={showDropdown}
                aria-haspopup="listbox"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={activeOptionId}
                aria-busy={isLoading}
                autoComplete="off"
                className={inputClassName}
                onChange={(event) => onChange(event.target.value)}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
            />
            <span className={styles.liveStatus} role="status" aria-live="polite" aria-atomic="true">
                {liveMessage}
            </span>
            {showDropdown && (
                <div className={styles.dropdown} role="listbox" id={listboxId}>
                    {isLoading && suggestions.length === 0 && (
                        <p className={styles.status}>Buscando...</p>
                    )}
                    {suggestions.map((item, index) => (
                        <button
                            type="button"
                            key={getOptionKey(item)}
                            id={`${listboxId}-option-${index}`}
                            role="option"
                            aria-selected={highlightedIndex === index}
                            className={highlightedIndex === index ? styles.optionActive : styles.option}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            onClick={() => selectOption(item)}
                        >
                            {renderOption(item)}
                        </button>
                    ))}
                    {hasSuggestions && showViewAll && (
                        <button
                            type="button"
                            id={`${listboxId}-option-${suggestions.length}`}
                            role="option"
                            aria-selected={highlightedIndex === suggestions.length}
                            className={highlightedIndex === suggestions.length ? styles.viewAllActive : styles.viewAll}
                            onMouseEnter={() => setHighlightedIndex(suggestions.length)}
                            onClick={submitSearch}
                        >
                            Ver todos los resultados para "{trimmedValue}"
                        </button>
                    )}
                    {showBroadHint && (
                        <p className={styles.broadHint}>
                            Búsqueda amplia: añade marca, modelo o categoría para resultados más rápidos.
                        </p>
                    )}
                    {!isLoading && !hasSuggestions && (
                        <p className={styles.status}>
                            {loadFailed
                                ? 'No se han podido cargar sugerencias. Pulsa Enter para buscar igualmente.'
                                : `Sin resultados todavía para "${trimmedValue}"`}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchAutocomplete;
